import type { KeywordNewsResult } from '../types/news.types'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Search Cache Types
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface SearchCacheEntry {
  keyword: string
  result: KeywordNewsResult
  timestamp: number
  expiresAt: number
  searchParams: {
    display: number
    sort: string
    start: number
  }
}

interface SearchCacheStore {
  entries: Record<string, SearchCacheEntry>
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Constants
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const CACHE_KEY = 'keynews-search-cache'
const CACHE_DURATION_MS = 60 * 60 * 1000 // 1 hour in milliseconds

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Cache Key Generation
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function generateCacheKey(
  keyword: string,
  params: { display: number; sort: string; start: number }
): string {
  return `${keyword}:${params.display}:${params.sort}:${params.start}`
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Storage Operations
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function getCacheStore(): SearchCacheStore {
  try {
    const stored = localStorage.getItem(CACHE_KEY)
    if (!stored) return { entries: {} }

    const parsed = JSON.parse(stored) as SearchCacheStore
    return parsed
  } catch (error) {
    console.error('Failed to parse search cache:', error)
    return { entries: {} }
  }
}

function saveCacheStore(store: SearchCacheStore): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(store))
  } catch (error) {
    console.error('Failed to save search cache:', error)
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Cache Validation
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function isCacheValid(entry: SearchCacheEntry): boolean {
  return Date.now() < entry.expiresAt
}

function cleanExpiredCache(store: SearchCacheStore): SearchCacheStore {
  const now = Date.now()
  const validEntries: Record<string, SearchCacheEntry> = {}

  Object.entries(store.entries).forEach(([key, entry]) => {
    if (now < entry.expiresAt) {
      validEntries[key] = entry
    }
  })

  return { entries: validEntries }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Public API
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const searchCache = {
  /**
   * Get cached search result if valid
   */
  get(
    keyword: string,
    params: { display: number; sort: string; start: number }
  ): KeywordNewsResult | null {
    const store = getCacheStore()
    const cacheKey = generateCacheKey(keyword, params)
    const entry = store.entries[cacheKey]

    if (!entry) return null
    if (!isCacheValid(entry)) {
      // Remove expired entry
      delete store.entries[cacheKey]
      saveCacheStore(store)
      return null
    }

    return entry.result
  },

  /**
   * Store search result in cache with 1-hour expiration
   */
  set(
    keyword: string,
    result: KeywordNewsResult,
    params: { display: number; sort: string; start: number }
  ): void {
    let store = getCacheStore()

    // Clean expired entries before adding new one
    store = cleanExpiredCache(store)

    const now = Date.now()
    const cacheKey = generateCacheKey(keyword, params)

    store.entries[cacheKey] = {
      keyword,
      result,
      timestamp: now,
      expiresAt: now + CACHE_DURATION_MS,
      searchParams: params,
    }

    saveCacheStore(store)
  },

  /**
   * Check if cache exists and is valid
   */
  has(keyword: string, params: { display: number; sort: string; start: number }): boolean {
    return this.get(keyword, params) !== null
  },

  /**
   * Clear specific keyword cache
   */
  clear(keyword: string): void {
    const store = getCacheStore()
    const keysToDelete: string[] = []

    Object.keys(store.entries).forEach((key) => {
      if (key.startsWith(`${keyword}:`)) {
        keysToDelete.push(key)
      }
    })

    keysToDelete.forEach((key) => {
      delete store.entries[key]
    })

    saveCacheStore(store)
  },

  /**
   * Clear all cached data
   */
  clearAll(): void {
    localStorage.removeItem(CACHE_KEY)
  },

  /**
   * Get cache statistics
   */
  getStats(): { totalEntries: number; validEntries: number; expiredEntries: number } {
    const store = getCacheStore()
    const now = Date.now()
    let validCount = 0
    let expiredCount = 0

    Object.values(store.entries).forEach((entry) => {
      if (now < entry.expiresAt) {
        validCount++
      } else {
        expiredCount++
      }
    })

    return {
      totalEntries: Object.keys(store.entries).length,
      validEntries: validCount,
      expiredEntries: expiredCount,
    }
  },
}
